import { erc20Abi, getAddress, zeroAddress } from 'viem';

import type { BalanceLightResponse, BalanceResponse } from '@/lib/api/speed';
import { hyperEvmPublicClient } from '@/lib/hyperEvmPublicClient';
import { hlInfo } from '@/lib/hyperliquidInfo';

/**
 * Reads the user's speed-trading balance from public infrastructure instead of asking the
 * backend to do it: one HyperEVM multicall for the USDC buckets plus one call to the public
 * Hyperliquid info API, both of which any client may call directly.
 *
 * `GET /speed/balance/light` supplies only what the chain cannot report — the resolved
 * addresses, the `unified` flag (mongo-only state), and the withdraw policy inputs.
 *
 * Every failure here is the caller's cue to fall back to the full `GET /speed/balance`,
 * which stays the authority. Nothing that MOVES money may be gated on this result: a
 * client-derived `withdrawableUsdc` is attacker-influenced and is for display only.
 */

const USDC_BASE_UNITS = 1_000_000;
const INFO_TIMEOUT_MS = 3_000;

/**
 * Hard ceiling on the whole on-chain read. Past this we stop waiting and let the caller fall
 * back to the backend, which already has the answer.
 *
 * The point is that reading it ourselves must never be SLOWER for the user than asking the
 * backend. Without a ceiling the failover only triggers on an error, so a merely-slow
 * provider chain degrades the balance instead of falling back — which is how a read that
 * measures ~700ms end to end turned into a five-second spinner.
 */
const ONCHAIN_READ_DEADLINE_MS = 2_500;

/** Log a phase breakdown when a read is slow enough for a user to notice. */
const SLOW_READ_LOG_THRESHOLD_MS = 1_200;

/** Multicall3's canonical CREATE2 address, verified deployed on HyperEVM 999. */
const MULTICALL3_ADDRESS = '0xcA11bde05977b3631167028862bE2a173976CA11' as const;

/**
 * Whether a failed `GET /speed/balance/light` should be retried as the full
 * `GET /speed/balance`.
 *
 * `null` = no HTTP answer at all (transport failure).
 *
 * A definitive business answer (custodialWalletMissing, unauthorized) is the SAME answer the
 * full endpoint gives, so retrying it would double the failing requests for exactly the users
 * who have no wallet yet — worse than the load this was meant to remove. 404 must fall back:
 * the frontend deploys ahead of factions, so that window is guaranteed on release.
 */
export function shouldFallBackToFullBalance(status: number | null): boolean {
    if (status === null) return true;
    if (status === 404) return true;
    return status >= 500;
}

/**
 * Shared default for the core-sweep reserve; mirrors factions and MoonHub.API.
 *
 * The name is historical (it dates from reading the reserve as transfer gas). It is the reserve
 * for HyperCore's one-time fee on an account's first transfer out ($1) plus the ~$0.002 transfer
 * gas per sweep — both come out of what stays on the core account, never out of the amount swept,
 * so the user sees them as core balance that is not withdrawable, not as a withdrawal fee.
 */
const DEFAULT_CORE_SWEEP_GAS_BUFFER_USD = 1.01;

/**
 * The core-sweep reserve in 6dp base units.
 *
 * MoonHub.API subtracts it in HyperCore's 8dp and floors the RESULT to 6dp; we only ever see
 * the already-floored 6dp figure, so the buffer rounds UP. Identical for any buffer
 * expressible in 6dp; otherwise we advertise at most one base unit less, never more.
 */
function coreSweepGasBufferBaseUnits(usd: number | undefined): bigint {
    const value =
        typeof usd === 'number' && Number.isFinite(usd) && usd >= 0
            ? usd
            : DEFAULT_CORE_SWEEP_GAS_BUFFER_USD;
    const core8 = BigInt(Math.round(value * 100_000_000));
    return (core8 + 99n) / 100n;
}

/**
 * Port of factions' `helpers/speedBalance.mjs#deriveBalance`. Deliberately duplicated rather
 * than approximated: the backend test pins these rules against a recorded response and so
 * does ours, so a change on either side surfaces as a failing parity test rather than as a
 * wrong number on screen.
 *
 * `withdrawableUsdc` is the number the withdraw panel offers as the maximum, so it has to be
 * what MoonHub.API — the service that actually executes the sweep — is willing to move. It
 * keeps a gas buffer out of every HyperCore exit sweep; advertising the raw holdings instead
 * is what let the UI offer $17.13 on 2026-08-23 for a sweep HyperCore then refused.
 */
export function deriveBalance(input: {
    custodialUsdc: bigint;
    adapterUsdc: bigint;
    hyperCoreWithdrawable: bigint;
    unified: boolean;
    policy: Pick<
        BalanceLightResponse,
        'coreFloorUsd' | 'evmFloorUsd' | 'coreSweepFeeUsd' | 'evmFeeUsd'
    > & { coreSweepGasBufferUsd?: number };
}) {
    const { custodialUsdc, adapterUsdc, hyperCoreWithdrawable, unified, policy } =
        input;
    const total = custodialUsdc + adapterUsdc + hyperCoreWithdrawable;

    // what a sweep may actually TAKE out of core. The reserve is still the user's core balance
    // (HyperCore spends it on the one-time first-transfer fee and transfer gas, never on the
    // swept amount), so it is taken off the advertised maximum only — the total stays the
    // truthful holdings figure.
    const buffer = coreSweepGasBufferBaseUnits(policy.coreSweepGasBufferUsd);
    const coreSweepable =
        hyperCoreWithdrawable > buffer ? hyperCoreWithdrawable - buffer : 0n;

    // core is reachable only once the SWEEPABLE amount clears the floor; sub-floor dust stays
    // visible in the total but no route can move it. The standard route only ever spends
    // EVM-held USDC, and never sweeps core, so the buffer does not apply to it.
    const coreUsable =
        unified &&
        coreSweepable >= BigInt(Math.round(policy.coreFloorUsd * USDC_BASE_UNITS));

    return {
        totalUsdc: total,
        withdrawableUsdc: coreUsable
            ? custodialUsdc + adapterUsdc + coreSweepable
            : custodialUsdc + adapterUsdc,
        minWithdrawUsd: coreUsable ? policy.coreFloorUsd : policy.evmFloorUsd,
        flatFeeUsd: coreUsable ? policy.coreSweepFeeUsd : policy.evmFeeUsd,
    };
}

/**
 * Hyperliquid decimal account values carry EIGHT decimals. Parse at that precision — the
 * subtraction below must happen before any truncation to USDC's six, or a hold with
 * non-zero 7th/8th decimals rounds the result off by a base unit against the backend.
 */
export function parseUsdcToCore8(value: string | number | undefined): bigint {
    const text = String(value ?? '').trim();
    if (!/^\d+(\.\d+)?$/.test(text)) {
        throw new Error(`unparseable USDC amount: ${text}`);
    }
    const [whole, fraction = ''] = text.split('.');
    return (
        BigInt(whole) * 100_000_000n + BigInt((fraction + '00000000').slice(0, 8))
    );
}

/** Floor 8dp Hyperliquid amounts to EVM USDC's 6dp, matching the backend. */
export function core8ToEvm6(core8: bigint): bigint {
    if (core8 < 0n) throw new Error('amount must not be negative');
    return core8 / 100n;
}

/** `"12.34"` → 6dp base units, via 8dp so truncation matches the backend exactly. */
export function usdcStringToBaseUnits(value: string | number): bigint {
    return core8ToEvm6(parseUsdcToCore8(value));
}

interface SpotBalance {
    coin?: string;
    total?: string;
    hold?: string;
}

/**
 * Withdrawable USDC from a unified (spot) clearinghouse state, in 6dp base units.
 *
 * Throws on the same states the backend rejects (missing balances array, an ambiguous
 * duplicate USDC row, a hold exceeding the total). The caller catches and reports 0, so a
 * malformed upstream amount fails closed rather than inflating a balance.
 */
export function unifiedWithdrawableFromSpotState(state: {
    balances?: SpotBalance[];
}): bigint {
    if (!Array.isArray(state?.balances)) {
        throw new Error('invalid unified-account balance state');
    }
    const balances = state.balances.filter(
        (b) => typeof b?.coin === 'string' && b.coin.toUpperCase() === 'USDC',
    );
    if (balances.length === 0) return 0n;
    if (balances.length !== 1) {
        throw new Error('ambiguous unified-account USDC balance');
    }
    const total = parseUsdcToCore8(balances[0].total);
    const hold = parseUsdcToCore8(balances[0].hold);
    if (hold > total) {
        throw new Error('unified-account USDC hold exceeds total');
    }
    return core8ToEvm6(total - hold);
}

async function fetchHyperliquidState(
    body: Record<string, unknown>,
    signal?: AbortSignal,
): Promise<unknown> {
    return hlInfo<unknown>(body, { signal, timeoutMs: INFO_TIMEOUT_MS });
}

/**
 * Build the same payload `GET /speed/balance` returns, from public sources only.
 * Throws on any read failure — the caller falls back to the backend.
 */
export async function readOnchainBalance(
    light: BalanceLightResponse,
    signal?: AbortSignal,
): Promise<BalanceResponse> {
    const custodial = getAddress(light.custodialWallet);
    const usdc = getAddress(light.usdc);
    const hasAdapter =
        !!light.adapter && getAddress(light.adapter) !== zeroAddress;
    const adapter = hasAdapter ? getAddress(light.adapter!) : null;

    const addresses = adapter ? [custodial, adapter] : [custodial];

    const startedAt = performance.now();
    let chainMs = 0;
    let infoMs = 0;

    const deadline = new Promise<never>((_, reject) =>
        setTimeout(
            () =>
                reject(
                    new Error(
                        `on-chain balance read exceeded ${ONCHAIN_READ_DEADLINE_MS}ms`,
                    ),
                ),
            ONCHAIN_READ_DEADLINE_MS,
        ),
    );

    const [balances, state] = await Promise.race([
        Promise.all([
            // one eth_call for every bucket. allowFailure stays false: a failed sub-call must
            // not read as a zero balance and quietly understate someone's funds.
            hyperEvmPublicClient
                .multicall({
                    multicallAddress: MULTICALL3_ADDRESS,
                    allowFailure: false,
                    contracts: addresses.map((address) => ({
                        address: usdc,
                        abi: erc20Abi,
                        functionName: 'balanceOf' as const,
                        args: [address] as const,
                    })),
                })
                .then((result) => {
                    chainMs = performance.now() - startedAt;
                    return result;
                }),
            adapter
                ? fetchHyperliquidState(
                    {
                        type: light.unified
                            ? 'spotClearinghouseState'
                            : 'clearinghouseState',
                        user: adapter,
                    },
                    signal,
                ).then((result) => {
                    infoMs = performance.now() - startedAt;
                    return result;
                })
                : null,
        ]),
        deadline,
    ]);

    const totalMs = performance.now() - startedAt;
    if (totalMs >= SLOW_READ_LOG_THRESHOLD_MS) {
        // names the slow leg outright, so a provider degrading in the wild is diagnosable
        // from a user's console instead of needing a repro.
        console.warn('[Balance] slow on-chain read', {
            totalMs: Math.round(totalMs),
            multicallMs: Math.round(chainMs),
            hyperliquidMs: Math.round(infoMs),
        });
    }

    const custodialUsdc = balances[0] as bigint;
    const adapterUsdc = adapter ? (balances[1] as bigint) : 0n;

    let hyperCoreWithdrawable = 0n;
    if (adapter && state) {
        try {
            hyperCoreWithdrawable = light.unified
                ? unifiedWithdrawableFromSpotState(
                      state as { balances?: SpotBalance[] },
                )
                : usdcStringToBaseUnits(
                    (state as { withdrawable?: string }).withdrawable || '0',
                );
        } catch (error) {
            // fail closed, exactly as the backend does: a malformed upstream amount must
            // never inflate a balance. Surfaced so a persistent parse failure is visible
            // rather than silently hiding real funds.
            console.error(
                '[Balance] failed to derive HyperCore withdrawable; reporting 0',
                { adapter, unified: light.unified },
                error,
            );
            hyperCoreWithdrawable = 0n;
        }
    }

    const derived = deriveBalance({
        custodialUsdc,
        adapterUsdc,
        hyperCoreWithdrawable,
        unified: light.unified,
        policy: light,
    });

    return {
        networkId: light.networkId,
        custodialWallet: light.custodialWallet,
        adapter: light.adapter,
        custodialUsdc: custodialUsdc.toString(),
        adapterUsdc: adapterUsdc.toString(),
        hyperCoreWithdrawable: hyperCoreWithdrawable.toString(),
        totalUsdc: derived.totalUsdc.toString(),
        withdrawableUsdc: derived.withdrawableUsdc.toString(),
        minWithdrawUsd: String(derived.minWithdrawUsd),
        flatFeeUsd: derived.flatFeeUsd.toFixed(2),
    };
}
